import { c as _c } from "react/compiler-runtime";
import React from 'react';
import PropTypes from 'prop-types';
import SuspenseWithLoader from 'components/consumer/SuspenseWithLoader';
import RatioContainer from 'components/consumer/RatioContainer';
import PDPDesktopHeroGallery from '../PDPDesktopHeroGallery';
import PDPDesktopGalleryRevamp from '../PDPDesktopGalleryRevamp';
import PDPKeySellingPoints from '../PDPKeySellingPoints';
import PDPDesktopMarxentConfigurator from '../../modular-configurator/PDPMarxentConfigurator';
import useIsGalleryRevampVariant from '../useIsGalleryRevampVariant';
import { getPaintedHero } from '../PDPDesktopHeroGallery/helpers/lastPaintedHero';

// Re-show the last painted hero instead of blanking. A suspension here lands on
// an already-painted gallery (a late userLocation re-keys every location-scoped
// query at once), so there is a hero to hold (CON-8543). Exported for layouts
// that render the gallery themselves.
export const PDPDesktopGalleryHoldFallback = t0 => {
  const $ = _c(3);
  const {
    tag
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getPaintedHero();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const heldSrc = t1;
  if (tag === "ksp") {
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="min-h-[90px]" />;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="mb-8 ml-auto mr-auto relative"><RatioContainer ratio={0.6666666666666666}>{heldSrc ? <img className="h-full w-full object-contain object-center select-none" src={heldSrc} alt="" aria-hidden="true" /> : null}</RatioContainer></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
PDPDesktopGalleryHoldFallback.propTypes = {
  tag: PropTypes.string
};
const PDPDesktopPhotoPanelInner = t0 => {
  const $ = _c(14);
  const {
    isEditOrder: t1,
    pdpIsModularBYOProduct: t2,
    slug,
    hideMarxent: t3,
    unavailableFabric: t4,
    productHeroImage: t5,
    productCylindoHeroImage: t6
  } = t0;
  const isEditOrder = t1 === undefined ? false : t1;
  const pdpIsModularBYOProduct = t2 === undefined ? false : t2;
  const hideMarxent = t3 === undefined ? true : t3;
  const unavailableFabric = t4 === undefined ? false : t4;
  const productHeroImage = t5 === undefined ? null : t5;
  const productCylindoHeroImage = t6 === undefined ? null : t6;
  const isGalleryRevampVariant = useIsGalleryRevampVariant();
  let t7;
  if ($[0] !== hideMarxent || $[1] !== isEditOrder || $[2] !== isGalleryRevampVariant || $[3] !== pdpIsModularBYOProduct || $[4] !== productCylindoHeroImage || $[5] !== productHeroImage || $[6] !== slug || $[7] !== unavailableFabric) {
    t7 = pdpIsModularBYOProduct ? <PDPDesktopMarxentConfigurator pdpIsModularBYOProduct={pdpIsModularBYOProduct} slug={slug} key={slug} hideMarxent={hideMarxent} unavailableFabric={unavailableFabric} /> : isGalleryRevampVariant ? <SuspenseWithLoader fallback={<PDPDesktopGalleryHoldFallback tag="revamp" />}><PDPDesktopGalleryRevamp slug={slug} productHeroImage={productHeroImage} productCylindoHeroImage={productCylindoHeroImage} /></SuspenseWithLoader> : <SuspenseWithLoader fallback={<PDPDesktopGalleryHoldFallback tag="gallery" />}><PDPDesktopHeroGallery isEditOrder={isEditOrder} pdpIsModularBYOProduct={pdpIsModularBYOProduct} slug={slug} productHeroImage={productHeroImage} productCylindoHeroImage={productCylindoHeroImage} /></SuspenseWithLoader>;
    $[0] = hideMarxent;
    $[1] = isEditOrder;
    $[2] = isGalleryRevampVariant;
    $[3] = pdpIsModularBYOProduct;
    $[4] = productCylindoHeroImage;
    $[5] = productHeroImage;
    $[6] = slug;
    $[7] = unavailableFabric;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== isEditOrder) {
    t8 = !isEditOrder && <SuspenseWithLoader fallback={<PDPDesktopGalleryHoldFallback tag="ksp" />}><PDPKeySellingPoints /></SuspenseWithLoader>;
    $[9] = isEditOrder;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t7 || $[12] !== t8) {
    t9 = <div className="sticky top-8">{t7}{t8}</div>;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  return t9;
};
PDPDesktopPhotoPanelInner.propTypes = {
  isEditOrder: PropTypes.bool,
  pdpIsModularBYOProduct: PropTypes.bool,
  slug: PropTypes.string,
  hideMarxent: PropTypes.bool,
  unavailableFabric: PropTypes.bool,
  productHeroImage: PropTypes.shape({
    src: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    alt: PropTypes.string
  }),
  productCylindoHeroImage: PropTypes.shape({
    src: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    alt: PropTypes.string
  })
};

// Outermost boundary: with no fallback this renders null, so a suspension in
// PDPDesktopPhotoPanelInner's own render, above the per-child boundaries,
// would blank hero, thumbnails and selling points together.
const PDPDesktopPhotoPanel = props => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <PDPDesktopGalleryHoldFallback tag="panel" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <SuspenseWithLoader fallback={t0}><PDPDesktopPhotoPanelInner {...props} /></SuspenseWithLoader>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
export default PDPDesktopPhotoPanel;